import { produce } from 'immer';
import { useCallback, useContext, useMemo } from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { GroupSettingsContext } from '../contexts/GroupSettingsContext';
import { updateDashboardFunc } from '../lib/helper';
import queryClient from '../services/queryClient';
import { CONNECTED_APPS, type ResolvedApp } from '../types/connectedApps';
import { ConnectedApp } from '../types/files';

const extractDomainFromUrl = (url: string): string | null => {
    try {
        return new URL(url).hostname;
    } catch {
        const match = url.match(/(?:https?:\/\/)?([^/\s]+)/i);
        return match?.[1] || null;
    }
};

const hashColor = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#4285F4', '#00A1E0', '#FF7A59', '#FF4A00', '#7c3aed', '#059669', '#D946EF', '#F59E0B', '#EF4444', '#6366F1'];
    return colors[Math.abs(hash) % colors.length];
};

const getAppColorStatic = (name: string): string => {
    const entry = CONNECTED_APPS[name.toLowerCase()];
    if (entry) return entry.color;
    return hashColor(name);
};

const useConnectedApps = () => {
    const { dashboard, setDashboard } = useContext(DashboardContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { connectedApps: groupConnectedApps, setConnectedApps: setGroupConnectedApps } = useContext(GroupSettingsContext);

    // ---- Resolution / display ----

    const resolveApp = useCallback((link?: string | null, name?: string): ResolvedApp => {
        // If name is defined and non-empty, resolve by name first
        if (name && name.trim()) {
            const entry = CONNECTED_APPS[name.toLowerCase()];
            if (entry) {
                return { name, icon: entry.icon, iconUrl: entry.iconUrl, color: entry.color, domain: entry.domain };
            }
        }

        // Only infer from link when name is empty/undefined
        if (link) {
            for (const config of Object.values(CONNECTED_APPS)) {
                if (config.linkPattern.test(link)) {
                    return { name: name || config.name, icon: config.icon, iconUrl: config.iconUrl, color: config.color, domain: config.domain };
                }
            }
        }

        const domainFromLink = link ? extractDomainFromUrl(link) : null;
        const fallbackDomain = domainFromLink || (name ? `${name.toLowerCase().trim().replace(/\s+/g, '')}.com` : '');
        const label = name || fallbackDomain;

        return { name: label, icon: undefined, color: getAppColorStatic(label), domain: fallbackDomain };
    }, []);

    const getDisplayName = useCallback((app: ConnectedApp): string => {
        if (app.name) return app.name;
        if (app.link) return resolveApp(app.link, app.name).name;
        return 'Unknown';
    }, [resolveApp]);

    const getAppInitials = useCallback((name: string): string =>
        name.split(' ').slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('')
        , []);

    const getAppLogoUrl = useCallback((domain: string, size: number = 128): string =>
        `https://logo.clearbit.com/${domain}?size=${size}`
        , []);

    const getGoogleFaviconUrl = useCallback((domain: string, size: number = 128): string =>
        `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`
        , []);

    // ---- Single-dashboard CRUD ----

    const dashboardApps = useMemo((): ConnectedApp[] =>
        dashboard?.connectedApps ?? []
        , [dashboard]);

    const updateAppLink = useCallback(async (index: number, newLink: string) => {
        if (!dashboard) return;

        const updated = produce(dashboard, (draft) => {
            if (!draft.connectedApps) draft.connectedApps = [];
            if (draft.connectedApps[index]) draft.connectedApps[index].link = newLink;
        });
        setDashboard(updated);
        setDashboards(prev => prev.map(d => d.id === updated.id ? updated : d));
        queryClient.setQueryData(['getDashboardById', updated.id], updated);
        await updateDashboardFunc(updated);
    }, [dashboard, setDashboard, setDashboards]);

    const removeApp = useCallback(async (app: ConnectedApp) => {
        if (!dashboard) return;

        const updated = produce(dashboard, (draft) => {
            if (!draft.connectedApps) draft.connectedApps = [];
            draft.connectedApps = draft.connectedApps.filter(a => a.link !== app.link);
        });
        setDashboard(updated);
        setDashboards(prev => prev.map(d => d.id === updated.id ? updated : d));
        queryClient.setQueryData(['getDashboardById', updated.id], updated);
        await updateDashboardFunc(updated);
    }, [dashboard, setDashboard, setDashboards]);
    // ---- Effective per-dashboard view (driven by group settings) ----

    /** Apps to display on a single dashboard: one entry per group-level app name,
     *  enriched with the per-dashboard link when available. */
    const effectiveApps = useMemo((): ConnectedApp[] => {
        return groupConnectedApps.map(name => {
            // Find matching entry in this dashboard's connectedApps (by name, case-insensitive)
            const dashEntry = dashboard?.connectedApps?.find(a =>
                (a.name || '').toLowerCase() === name.toLowerCase()
            );
            return { name, link: dashEntry?.link ?? null };
        });
    }, [groupConnectedApps, dashboard]);

    /** Update the link for a specific app by name (upserts in dashboard.connectedApps).
     *  The app name must exist in the group-level connectedApps list. */
    const updateAppLinkByName = useCallback(async (appName: string, newLink: string) => {
        if (!dashboard) return;

        // Only allow updates for apps registered in group settings
        const isGroupApp = groupConnectedApps.some(n =>
            n.toLowerCase() === appName.toLowerCase()
        );
        if (!isGroupApp) return;

        const updated = produce(dashboard, (draft) => {
            if (!draft.connectedApps) draft.connectedApps = [];
            const existing = draft.connectedApps.find(a =>
                (a.name || '').toLowerCase() === appName.toLowerCase()
            );
            if (existing) {
                existing.link = newLink;
            } else {
                draft.connectedApps.push({ name: appName, link: newLink });
            }
        });
        setDashboard(updated);
        // Sync the dashboards list so the homepage table reflects the change immediately
        setDashboards(prev => prev.map(d => d.id === updated.id ? updated : d));
        // Update the React Query cache so re-navigating to this dashboard doesn't
        // restore stale data from the 30s ensureQueryData cache.
        queryClient.setQueryData(['getDashboardById', updated.id], updated);
        await updateDashboardFunc(updated);
    }, [dashboard, setDashboard, setDashboards, groupConnectedApps]);


    /** Returns the list of connected app names from group settings. */
    const allUniqueApps = useCallback((): { name: string; link?: string | null; count: number }[] => {
        return groupConnectedApps.map(name => ({ name, count: 0 }));
    }, [groupConnectedApps]);

    /** Returns the connected apps from group settings, for inheriting into new dashboards. */
    const getGlobalConnectedApps = useCallback((): ConnectedApp[] => {
        return groupConnectedApps.map(name => ({ name }));
    }, [groupConnectedApps]);

    /** Add an app to the group-level list (no dashboard updates). */
    const addAppToAll = useCallback((app: ConnectedApp) => {
        const name = app.name?.trim();
        if (!name) return;
        // Deduplicate
        if (groupConnectedApps.some(n => n.toLowerCase() === name.toLowerCase())) return;
        setGroupConnectedApps(prev => [...prev, name]);
    }, [groupConnectedApps, setGroupConnectedApps]);

    /** Remove an app from the group-level list (no dashboard updates). */
    const removeAppFromAll = useCallback((name: string) => {
        setGroupConnectedApps(prev => prev.filter(n => n.toLowerCase() !== name.toLowerCase()));
    }, [setGroupConnectedApps]);

    return {
        resolveApp,
        getDisplayName,
        getAppInitials,
        getAppLogoUrl,
        getGoogleFaviconUrl,
        dashboardApps,
        effectiveApps,
        updateAppLink,
        updateAppLinkByName,
        removeApp,
        allUniqueApps,
        addAppToAll,
        removeAppFromAll,
        getGlobalConnectedApps,
    };
};

export default useConnectedApps;
